import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Stack,
    useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { getMasterListAliasSuggestions } from '../../services/masterlistAlias';
import TypeaheadWidget from '../../CommonComponents/TypeaheadWidget';
import { createNewVirtualTour, getVirtualToursData, updateVirtualTourData } from '../../services/VirtualTours';
import { reverseTransformData, transformData } from '../../util/helper';
import { handleApiError, showSuccessToast } from '../../util/errorHandler';

const VirtualTour = () => {

    const { alias } = useParams();
    const [processedData, setProcessedData] = useState({});
    const [quickSave, setQuickSave] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const schema = {
        "title": "Virtual Tour Fields",
        "description": "",
        "type": "object",
        "properties": {
            masterlist_alias: {
                "type": "string",
                "title": "Masterlist Alias",
                "default": ""
            },
            materport_link: {
                type: "string",
                title: "Meterport Link",
                default: ""
            },
        },
        required: ['masterlist_alias']
    };

    const uiSchema = {
        "ui:submitButtonOptions": {
            "submitText": "Save",
            "norender": false,
            "props": {
                "disabled": false,
                "className": "button button--primary button--blue",
                "loadingText": "Saving..",
                "colorScheme": "blue"
            }
        },

        masterlist_alias: {
            'ui:emptyValue': '',
            'ui:readonly': !!alias,
            'ui:widget': 'masterlistAliasTypeahead'
        },
        materport_link: {
            "ui:emptyValue": ""
        },
    };

    const formData = processedData;

    // Fetch initial data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (alias) {
                    const result = await getVirtualToursData(alias);
                    if (result && result?.data?.[0]) {

                        const fieldsToTransform = [];
                        const transformedData = transformData(result?.data[0], fieldsToTransform);

                        // To Show image on form need to provide url to image field which is only used to show the image
                        setProcessedData(transformedData);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch data"
                });
            }
        };

        getBasicData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alias, quickSave]);


    const loadMasterlistAliasSuggestions = async (inputValue) => {
        try {
            const result = await getMasterListAliasSuggestions(20, 1, inputValue);
            return result?.data?.data || [];
        } catch (error) {
            handleApiError(error, toast, {
                defaultErrorMessage: "Failed to load document types"
            });
            return [];
        }
    };

    const fetchMasterlistAliasSuggestions = async (inputValue) => {

        const fetchedMasterListAliasSuggestions = await loadMasterlistAliasSuggestions(inputValue);

        // Ensure inputValue is string and trimmed
        if (!inputValue || typeof inputValue !== "string" || !fetchedMasterListAliasSuggestions) {
            return [];
        }

        return fetchedMasterListAliasSuggestions?.map((item) => ({
            label: item.alias,
            value: item.alias
        }));
    };


    // Submit updated data
    const handleSubmit = async (formData) => {

        const updatedFormData = reverseTransformData(formData, []);

        try {
            if (alias) {
                const result = await updateVirtualTourData(alias, updatedFormData);

                if (result?.status === 200) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
                }
            } else {
                const result = await createNewVirtualTour(updatedFormData);  // Call the create API
                if (result?.status === 201) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New Virtual Tour created successfully");
                    navigate(`/virtual-tours/${result?.data?.masterlist_alias}`);  // Navigate to the newly created post
                }
            }
        } catch (error) {
            handleApiError(error, toast);
        }
    };


    return (
        <Stack flexDirection={'row'} gap={"15px"} alignItems={"flex-start"}>
            <Box width={"100%"}>
                <Form
                    schema={schema}
                    uiSchema={uiSchema}
                    formData={formData}
                    validator={validator}
                    onSubmit={(e) => handleSubmit(e.formData)}
                    widgets={{
                        masterlistAliasTypeahead: (props) => (
                            <TypeaheadWidget
                                {...props}
                                fetchOptions={fetchMasterlistAliasSuggestions}
                            />
                        ),
                    }}
                />
            </Box>
        </Stack>
    );
};

export default VirtualTour;